import {
  ChatExhibitors,
  Message,
  MessageFile,
  SenderInfo,
  StandsWithCompanies,
  UploadFile,
  UserInfo,
} from "ts/api/chat";
import { apiV2, db, DocumentData, FieldValue, storage } from "../firebase";

export const getExtension = (fullFileName: string) => {
  const lastDotIndex = fullFileName.lastIndexOf(".");
  if (lastDotIndex <= 0) {
    return "";
  }

  const extension = fullFileName.substring(lastDotIndex + 1);

  return extension ? `.${extension}` : "";
};

const messagingService = {
  createOrUpdateStandToOrganisersChat: async (
    showId: string,
    item: StandsWithCompanies,
    data: UserInfo,
    senderId: string,
    message?: string
  ) => {
    const chatId = `${item.stand.standId}-organisers`;
    const standConversation = {
      [item.stand.standId]: {
        organisers: {
          lastMessage: message || "",
          unreadCount: FieldValue.increment(
            senderId !== item.stand.standId ? 1 : 0
          ),
          userInfo: data,
          date: FieldValue.serverTimestamp(),
        },
      },
    };

    const organisersConversation = {
      organisers: {
        [item.stand.standId]: {
          userInfo: data,
          lastMessage: message || "",
          unreadCount: FieldValue.increment(
            senderId === item.stand.standId ? 1 : 0
          ),
          date: FieldValue.serverTimestamp(),
        },
      },
    };
    if (!item.stand.standId) {
      return;
    }
    try {
      const showRef = db.collection("shows").doc(showId);

      const showSnap = await showRef.get();
      await db
        .collection("shows")
        .doc(showId)
        .collection("standsConversations")
        .doc(item.stand.standId)
        .set(standConversation, { merge: true });

      await db
        .collection("shows")
        .doc(showId)
        .collection("standsConversations")
        .doc("organisers")
        .set(organisersConversation, { merge: true });

      const conversationDocRef = db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(chatId);
      const docSnapshot = await conversationDocRef.get();
      if (showSnap.exists && !docSnapshot.exists && item?.pairs?.length) {
        const showData = showSnap.data();

        const chatExhibitors: ChatExhibitors = {};
        const chatOrganiser: ChatExhibitors = {};
        const allConnections = await apiV2.exhibitor.userConnection.getAllByStand(
          {
            showId,
            exhibitorPairId: item.pairs[0].id,
          }
        );

        if (allConnections?.length) {
          allConnections.forEach((exhibitor) => {
            chatExhibitors[exhibitor.exhibitor_user_id] = {};
          });
        }
        if (showData?.organisers?.length) {
          showData?.organisers?.forEach((organiser: Record<string, string>) => {
            chatOrganiser[organiser.id] = {};
          });
        }
        const conversation = {
          exhibitors: chatExhibitors,
        };

        await conversationDocRef.set(conversation, { merge: true });
      }
    } catch (error) {
      console.error("Error creating or updating conversation", error);
    }
  },
  sendStandToOrganisersMessage: async (
    showId: string,
    item: StandsWithCompanies,
    senderInfo: SenderInfo,
    senderId: string,
    text: string,
    files?: UploadFile[],
    read?: boolean
  ) => {
    const fileMap = files?.map((file) => ({
      name: file.name,
      url: file.url,
      id: file.id,
    }));

    if (!item.stand.standId) {
      return;
    }
    const chatId = `${item.stand.standId}-organisers`;

    try {
      const messageDocRef = db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(chatId)
        .collection("messages")
        .doc();

      const messageId = messageDocRef.id;

      const message = {
        stand: item.stand.standId,
        text,
        senderInfo,
        date: new Date(),
        files: fileMap,
        read,
        id: messageId,
      };
      await messageDocRef.set(message);

      messagingService.createOrUpdateStandToOrganisersChat(
        showId,
        item,
        senderInfo,
        senderId,
        text
      );
    } catch (error) {
      console.error("Error creating or updating conversation", error);
    }
  },
  editStandToOrganisersMessage: async (
    showId: string,
    standId: string,
    messageId: string,
    text: string,
    files?: UploadFile[]
  ) => {
    const chatId = `${standId}-organisers`;
    const fileMap = files?.map((file) => ({
      name: file.name,
      url: file.url,
      id: file.id,
    }));
    try {
      const messageDocRef = db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(chatId)
        .collection("messages")
        .doc(messageId);

      const messageDoc = await messageDocRef.get();
      if (messageDoc.exists) {
        const oldMessage = messageDoc.data();
        const message = fileMap?.length
          ? {
              files: [...oldMessage?.files, ...fileMap],
              text,
              isEdited: true,
            }
          : {
              text,
              isEdited: true,
            };

        await messageDocRef.update(message);
      }
    } catch (error) {
      console.error("Error in updating", error);
    }
  },
  updateLastSeenExhibitor: async (
    showId: string,
    standId: string,
    userId: string
  ) => {
    const chatId = `${standId}-organisers`;
    const conversationDocRef = db
      .collection("shows")
      .doc(showId)
      .collection("conversations")
      .doc(chatId);
    try {
      await conversationDocRef.set(
        {
          exhibitors: {
            [userId]: { lastSeen: FieldValue.serverTimestamp() },
          },
        },
        { merge: true }
      );
    } catch (e) {
      console.error("Error updating exhibitor last seen", e);
    }
  },

  updateLastSeenOrganiser: async (
    showId: string,
    items: StandsWithCompanies[],
    userId: string
  ) => {
    try {
      if (items?.length) {
        items.forEach((item) => {
          const chatId = `${item.stand.standId}-organisers`;
          const conversationDocRef = db
            .collection("shows")
            .doc(showId)
            .collection("conversations")
            .doc(chatId);
          conversationDocRef.set(
            {
              organisers: {
                [userId]: { lastSeen: FieldValue.serverTimestamp() },
              },
            },
            { merge: true }
          );
        });
      }
    } catch (e) {
      console.error("Error updating organiser last seen", e);
    }
  },

  refreshUnread: async (
    showId: string,
    standId: string,
    orgUnread: number,
    standUnread: number
  ) => {
    const standConversation = {
      [standId]: {
        organisers: {
          unreadCount: standUnread,
        },
      },
    };

    const organisersConversation = {
      organisers: {
        [standId]: {
          unreadCount: orgUnread,
        },
      },
    };

    try {
      await db
        .collection("shows")
        .doc(showId)
        .collection("standsConversations")
        .doc(standId)
        .set(standConversation, { merge: true });

      await db
        .collection("shows")
        .doc(showId)
        .collection("standsConversations")
        .doc("organisers")
        .set(organisersConversation, { merge: true });
    } catch (error) {
      console.error("Error updating update", error);
    }
  },
  softDeleteStandToOrganisersMessage: async (
    showId: string,
    standId: string,
    messageId: string
  ) => {
    const chatId = `${standId}-organisers`;
    const messageDocRef = db
      .collection("shows")
      .doc(showId)
      .collection("conversations")
      .doc(chatId)
      .collection("messages")
      .doc(messageId);

    try {
      await messageDocRef.update({ text: "", isDeleted: true });

      const messagesQuerySnapshot = await db
        .collection("shows")
        .doc(showId)
        .collection("conversations")
        .doc(chatId)
        .collection("messages")
        .orderBy("date", "desc")
        .get();

      let lastMessage: Message | DocumentData | null = null;
      messagesQuerySnapshot.forEach((doc) => {
        const message = doc.data();
        if (!lastMessage && !message.isDeleted) {
          lastMessage = message as DocumentData;
        }
      });
      if (lastMessage) {
        const lastMessageInfo = {
          lastMessage: (lastMessage as Message).text,
          date: (lastMessage as Message).date,
        };

        await db
          .collection("shows")
          .doc(showId)
          .collection("standsConversations")
          .doc(standId)
          .set({ [standId]: { organisers: lastMessageInfo } }, { merge: true });

        await db
          .collection("shows")
          .doc(showId)
          .collection("standsConversations")
          .doc("organisers")
          .set({ organisers: { [standId]: lastMessageInfo } }, { merge: true });
      }
    } catch (error) {
      console.error("Error in soft-deleting message", error);
    }
  },
  uploadFile: (file: File, showId: string, standId: string, fileId: string) => {
    const storageRef = storage.ref();

    const extension = getExtension(file.name || "");
    const fileRef = storageRef.child(
      `shows/${showId}/conversations/${standId}/${fileId}${extension}`
    );

    return fileRef.put(file);
  },
  deleteFileMessage: async (
    showId: string,
    standId: string,
    messageId: string,
    fileId: string,
    fileName: string
  ) => {
    const chatId = `${standId}-organisers`;
    const messageDocRef = db
      .collection("shows")
      .doc(showId)
      .collection("conversations")
      .doc(chatId)
      .collection("messages")
      .doc(messageId);

    try {
      const messageDoc = await messageDocRef.get();
      if (!messageDoc.exists) {
        throw new Error("Message does not exist.");
      }

      let messageData = messageDoc.data();
      const updatedFiles = messageData?.files?.filter(
        (file: MessageFile) => file.id !== fileId
      );
      await messageDocRef.update({ files: updatedFiles });
      const extension = getExtension(fileName || "");

      const fileRef = storage.ref(
        `shows/${showId}/conversations/${standId}/${fileId}${extension}`
      );
      await fileRef.delete();
    } catch (error) {
      console.error("Error deleting file reference in message", error);
    }
  },

  deleteFileNotLoaded: async (
    showId: string,
    standId: string,
    fileId: string,
    fileName: string
  ) => {
    try {
      const extension = getExtension(fileName || "");
      const fileRef = storage.ref(
        `shows/${showId}/conversations/${standId}/${fileId}${extension}`
      );
      await fileRef.delete();
    } catch (error) {
      console.error("Error deleting file reference in message", error);
    }
  },
  updateMessageReadStatus: async (
    showId: string,
    standId: string,
    messageId: string,
    isOrganiser?: boolean
  ) => {
    const chatId = `${standId}-organisers`;
    const messageDocRef = db
      .collection("shows")
      .doc(showId)
      .collection("conversations")
      .doc(chatId)
      .collection("messages")
      .doc(messageId);

    try {
      await db.runTransaction(async (transaction) => {
        transaction.update(messageDocRef, { read: true });

        const standsConversationsDocRef = db
          .collection("shows")
          .doc(showId)
          .collection("standsConversations")
          .doc(isOrganiser ? "organisers" : standId);

        const standsConversationDoc = await standsConversationsDocRef.get();
        if (standsConversationDoc.exists) {
          const unreadCountField = isOrganiser
            ? `organisers.${standId}.unreadCount`
            : `${standId}.organisers.unreadCount`;

          const data = standsConversationDoc.data();
          const currentUnreadCount = isOrganiser
            ? data?.organisers?.[standId]?.unreadCount
            : data?.[standId]?.organisers?.unreadCount || 0;

          if (currentUnreadCount > 0) {
            transaction.update(standsConversationsDocRef, {
              [unreadCountField]: FieldValue.increment(-1),
            });
          }
        }
      });
    } catch (error) {
      console.error("Error updating message read status", error);
    }
  },
};

export default messagingService;
